import { resolveHeatmapDisplayMode, countUnplottedHeatmapSamples } from '../../helpers/reports/heatmapCoordinates';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Slider, Button, Space, Typography, Select, Alert } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { ReportHeatmap } from './ReportHeatmap';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { MetricLink } from '../MetricLink';
import { resolveColorRangesForMetric, resolveFieldRange } from '../../helpers/metricRules';

const { Text } = Typography;

interface AnimatedHeatmapPoint {
    x: number;
    y: number;
    value: number;
    gpsLat?: number;
    gpsLon?: number;
}

interface AnimatedHeatmapFrame {
    timestamp: string;
    displayDate: string;
    dataPoints: AnimatedHeatmapPoint[];
    grid?: {
        resolutionX: number;
        resolutionY: number;
        cells: Array<{ x: number; y: number; value: number | null }>;
    };
    value: number | null; // average for this time bucket
    unit?: string | null;
}

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    onAreaClick?: (areaId: number) => void;
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

export const ReportAnimatedHeatmap: React.FC<CommonSectionProps> = ({
    section,
    data,
    areas,
    areaTypes,
    metrics,
    previewAreaId,
    onMetricClick,
}) => {
    // React hooks MUST be called unconditionally (before any returns)
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per frame
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Find area info for mode determination
    const area = useMemo(() => areas.find((a: any) => a.id === previewAreaId), [areas, previewAreaId]);
    const areaType = useMemo(() => areaTypes.find((at: any) => at.id === area?.areaType?.id), [areaTypes, area]);
    const areaById = useMemo(() => {
        const map = new Map<number, any>();
        areas.forEach((item: any) => {
            if (item?.id) map.set(item.id, item);
        });
        return map;
    }, [areas]);
    const metricByKey = useMemo(() => {
        const map = new Map<string, any>();
        metrics.forEach((metric: any) => {
            if (metric?.key) map.set(metric.key, metric);
        });
        return map;
    }, [metrics]);
    
    // Check if this area has children (is a parent area)
    const hasChildren = useMemo(() => {
        return areas.some((a: any) => a.parentAreaId === previewAreaId);
    }, [areas, previewAreaId]);
    
    // Determine mode and template info
    const mode = useMemo(() => resolveHeatmapDisplayMode(
        (Array.isArray(data) ? data : []).filter(row => row.areaId == null || row.areaId === previewAreaId), areaType
    ), [data, previewAreaId, areaType]);

    const templateImageUrl = useMemo(() => {
        if (mode === 'template' && areaType?.id) {
            return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-image`;
        }
        return undefined;
    }, [mode, areaType]);

    const templateLinesUrl = useMemo(() => {
        console.log('🎨 [AnimatedHeatmap] Template Lines URL Construction:', {
            mode,
            areaTypeId: areaType?.id,
            hasTemplateLinesImage: !!(areaType as any)?.templateLinesImage,
            templateLinesImageValue: (areaType as any)?.templateLinesImage,
        });
        
        if (mode === 'template' && areaType?.id && (areaType as any).templateLinesImage) {
            const url = `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-lines-image`;
            console.log('✅ [AnimatedHeatmap] Lines URL generated:', url);
            return url;
        }
        
        console.log('❌ [AnimatedHeatmap] Lines URL NOT generated - conditions not met');
        return undefined;
    }, [mode, areaType]);

    const templateSize = useMemo(() => {
        return (areaType as any)?.templateDimensions || { width: 100, height: 100 };
    }, [areaType]);

    const geoJsonPreview = useMemo(() => {
        const geom = area?.geom;
        return geom ? {
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                geometry: geom,
                properties: {},
            }],
        } : undefined;
    }, [area]);

    const scopedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.filter((item: any) => item.areaId === undefined || item.areaId === null || item.areaId === previewAreaId);
    }, [data, previewAreaId]);

    // Normalize API frames for rendering. The legacy flat-row fallback allows the
    // frontend to remain usable while backend and frontend revisions roll over.
    const frames = useMemo((): AnimatedHeatmapFrame[] => {
        console.log('🎬 Pre-calculating animated heatmap frames...');
        console.log('🎬 Raw data received:', scopedData);
        console.log('🎬 Data is array?', Array.isArray(scopedData), 'Length:', scopedData?.length);
        
        if (!Array.isArray(scopedData) || scopedData.length === 0) {
            console.log('❌ No data or not array');
            return [];
        }

        const formatDisplayDate = (timestamp: string) => {
            const date = new Date(timestamp);
            return isNaN(date.getTime())
                ? timestamp
                : date.toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
        };

        const toDisplayPoint = (item: any, isFramePoint: boolean): AnimatedHeatmapPoint | null => {
            const rawValue = isFramePoint ? item.value : (item.average ?? item.avg ?? item.value);
            const value = Number(rawValue);
            if (rawValue === null || rawValue === undefined || !Number.isFinite(value)) return null;

            const rawX = isFramePoint ? item.x : item.xLocal;
            const rawY = isFramePoint ? item.y : item.yLocal;
            const gpsLat = Number(item.gpsLat);
            const gpsLon = Number(item.gpsLon);
            const hasGpsCoordinates = item.gpsLat !== null && item.gpsLat !== undefined &&
                item.gpsLon !== null && item.gpsLon !== undefined &&
                Number.isFinite(gpsLat) && Number.isFinite(gpsLon);

            if (hasGpsCoordinates) {
                return {
                    x: Number(rawX ?? 0),
                    y: Number(rawY ?? 0),
                    value,
                    gpsLat,
                    gpsLon,
                };
            }

            if (rawX === null || rawX === undefined || rawY === null || rawY === undefined) {
                return null;
            }

            const x = Number(rawX);
            const y = Number(rawY);
            return Number.isFinite(x) && Number.isFinite(y) ? { x, y, value } : null;
        };

        const isFrameResponse = scopedData.every((item: any) => Array.isArray(item?.dataPoints));
        if (isFrameResponse) {
            const normalizedFrames = scopedData
                .map((frame: any): AnimatedHeatmapFrame | null => {
                    const timestamp = String(frame.bucket ?? frame.timestamp ?? '');
                    if (!timestamp) return null;

                    const points = frame.dataPoints
                        .map((item: any) => toDisplayPoint(item, true))
                        .filter((point: AnimatedHeatmapPoint | null): point is AnimatedHeatmapPoint => point !== null);
                    const calculatedAverage = points.length > 0
                        ? points.reduce((sum: number, point: AnimatedHeatmapPoint) => sum + point.value, 0) / points.length
                        : null;
                    const responseAverage = Number(frame.average);
                    const firstUnit = frame.dataPoints.find((item: any) => item?.unit)?.unit;

                    return {
                        timestamp,
                        displayDate: formatDisplayDate(timestamp),
                        dataPoints: points,
                        grid: frame.grid,
                        value: frame.average !== null && frame.average !== undefined && Number.isFinite(responseAverage)
                            ? responseAverage
                            : calculatedAverage,
                        unit: firstUnit ?? null,
                    };
                })
                .filter((frame: AnimatedHeatmapFrame | null): frame is AnimatedHeatmapFrame => frame !== null)
                .sort((a: AnimatedHeatmapFrame, b: AnimatedHeatmapFrame) =>
                    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                );

            console.log(`✅ Normalized ${normalizedFrames.length} animated heatmap frames`);
            console.log('🎬 Sample frame:', normalizedFrames[0]);
            return normalizedFrames;
        }

        // Legacy response: group flat spatial-point rows by time bucket.
        const bucketMap = new Map<string, any[]>();
        
        scopedData.forEach((item: any) => {
            const bucket = item.bucket || item.x || item.timestamp;
            if (!bucket) return;
            
            const key = String(bucket);
            if (!bucketMap.has(key)) {
                bucketMap.set(key, []);
            }
            bucketMap.get(key)!.push(item);
        });

        // Sort buckets by time
        const sortedBuckets = Array.from(bucketMap.entries()).sort((a, b) => {
            const dateA = new Date(a[0]);
            const dateB = new Date(b[0]);
            return dateA.getTime() - dateB.getTime();
        });

        const calculatedFrames = sortedBuckets.map(([timestamp, bucketData]) => {
            const points: AnimatedHeatmapPoint[] = [];
            let totalValue = 0;
            let valueCount = 0;
            let grid: any = undefined;
            let unit: string | null = null;

            bucketData.forEach((item: any) => {
                const point = toDisplayPoint(item, false);
                if (!point) return;

                totalValue += point.value;
                valueCount++;
                unit = unit ?? item.unit ?? null;

                if (item.grid && !grid) {
                    grid = item.grid;
                }

                points.push(point);
            });

            const avgValue = valueCount > 0 ? totalValue / valueCount : null;

            return {
                timestamp,
                displayDate: formatDisplayDate(timestamp),
                dataPoints: points,
                grid,
                value: avgValue,
                unit,
            };
        });

        console.log(`✅ Pre-calculated ${calculatedFrames.length} frames`);
        console.log('🎬 Sample frame:', calculatedFrames[0]);
        return calculatedFrames;
    }, [scopedData]);

    // Calculate global min/max across ALL frames for consistent color scaling
    const globalValueRange = useMemo(() => {
        if (frames.length === 0) {
            return { min: 0, max: 100 };
        }

        let allValues: number[] = [];
        frames.forEach(frame => {
            frame.dataPoints.forEach(point => {
                if (point.value !== null && point.value !== undefined) {
                    allValues.push(point.value);
                }
            });
        });

        if (allValues.length === 0) {
            return { min: 0, max: 100 };
        }

        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        
        console.log('🎨 Global value range for all frames:', { min, max });
        return { min, max };
    }, [frames]);

    const fixedValueRange = section.heatmapValueRangeMode === 'fixed' &&
        section.heatmapValueMin !== null &&
        section.heatmapValueMin !== undefined &&
        section.heatmapValueMax !== null &&
        section.heatmapValueMax !== undefined
        ? {
            mode: 'fixed' as const,
            min: section.heatmapValueMin,
            max: section.heatmapValueMax,
        }
        : null;

    const areaDefaultValueRange = useMemo(() => {
        const metricKey = section.metrics?.[0];
        const metricConfig = metricKey ? metricByKey.get(metricKey) : null;
        const targetArea = previewAreaId ? areaById.get(previewAreaId) : area;
        const range = resolveFieldRange(resolveColorRangesForMetric(metricConfig, targetArea), 'value');
        if (
            !range ||
            range.min === null ||
            range.min === undefined ||
            range.max === null ||
            range.max === undefined
        ) {
            return null;
        }
        return {
            mode: 'fixed' as const,
            min: range.min,
            max: range.max,
        };
    }, [section.metrics, metricByKey, previewAreaId, areaById, area]);

    // Auto-play effect
    useEffect(() => {
        if (!isPlaying || frames.length === 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        intervalRef.current = setInterval(() => {
            setCurrentFrameIndex((prev) => {
                // Loop back to start when reaching end
                return (prev + 1) % frames.length;
            });
        }, playbackSpeed);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isPlaying, playbackSpeed, frames.length]);

    // Reset to first frame when data changes
    useEffect(() => {
        setCurrentFrameIndex(0);
    }, [data]);

    const currentFrame = frames[currentFrameIndex];
    const currentUnit = currentFrame?.unit;
    const sectionTitle = section.title || 'Animated heatmap';
    const sectionMeta = useMemo(() => {
        const areaName = area?.name || 'No area';
        if (frames.length === 0) return areaName;
        const firstFrame = frames[0];
        const lastFrame = frames[frames.length - 1];
        return `${areaName} · ${firstFrame.displayDate} → ${lastFrame.displayDate}`;
    }, [area, frames]);
    const resolvedValueRange = useMemo(() => {
        if (section.heatmapValueRangeMode === 'areaDefault' && areaDefaultValueRange) {
            return areaDefaultValueRange;
        }
        if (fixedValueRange) {
            return fixedValueRange;
        }
        return {
            mode: 'fixed' as const,
            min: globalValueRange.min,
            max: globalValueRange.max,
        };
    }, [section.heatmapValueRangeMode, areaDefaultValueRange, fixedValueRange, globalValueRange]);

    // Create metric links for the animated heatmap
    const metricLinks = useMemo(() => {
        if (!section.metrics || section.metrics.length === 0 || !previewAreaId) {
            return null;
        }
        
        return (
            <Space size="small">
                {section.metrics.map((metricKey: string, metricIdx: number) => {
                    const metric = metrics.find(m => m.key === metricKey);
                    const metricName = metric?.name || metricKey;
                    
                    if (onMetricClick && previewAreaId) {
                        return (
                            <React.Fragment key={metricKey}>
                                {metricIdx > 0 && <Text type="secondary" style={{ fontSize: 12 }}> · </Text>}
                                <MetricLink
                                    metricKey={metricKey}
                                    metricName={metricName}
                                    metricId={metric?.id}
                                    areaId={previewAreaId}
                                    onClick={onMetricClick}
                                    style={{ fontSize: 12 }}
                                />
                            </React.Fragment>
                        );
                    }
                    return (
                        <Text key={metricKey} style={{ fontSize: 12 }}>
                            {metricIdx > 0 && ' · '}{metricName}
                        </Text>
                    );
                })}
            </Space>
        );
    }, [section.metrics, metrics, previewAreaId, onMetricClick]);

    // Block parent areas AFTER all hooks - only leaf areas supported for heatmaps
    if (hasChildren) {
        return (
            <Alert 
                message="Parent Area Not Supported" 
                description="Animated heatmaps can only display data for leaf areas (areas without children). Please select a specific child area to view the heatmap visualization."
                type="warning" 
                showIcon 
            />
        );
    }

    if (frames.length === 0) {
        return (
            <Typography.Text type="secondary">No time-bucketed data available</Typography.Text>
        );
    }

    return (
        <div className="report-animated-heatmap">
            <div className="report-animated-heatmap-head">
                <div className="report-animated-heatmap-title">{sectionTitle}</div>
                <div className="report-animated-heatmap-meta">{sectionMeta}</div>
            </div>

            <div className="report-animated-heatmap-summary">
                <div className="report-animated-heatmap-summary-main">
                    {metricLinks ? (
                        <div className="report-animated-heatmap-links">{metricLinks}</div>
                    ) : null}
                    {currentFrame?.value !== null && currentFrame?.value !== undefined ? (
                        <Text type="secondary" className="report-animated-heatmap-range">
                            Avg: {currentFrame.value.toFixed(2)}
                            {currentUnit ? ` ${currentUnit}` : ''}
                            {' '}·{' '}Range: {resolvedValueRange.min?.toFixed(2)} - {resolvedValueRange.max?.toFixed(2)}
                            {currentUnit ? ` ${currentUnit}` : ''}
                        </Text>
                    ) : null}
                </div>
                <div className="report-animated-heatmap-summary-side">
                    <span className="report-animated-heatmap-frame-pill">
                        {currentFrame?.displayDate}
                    </span>
                    <span className="report-animated-heatmap-frame-count">
                        Frame {currentFrameIndex + 1} of {frames.length}
                    </span>
                </div>
            </div>

            {countUnplottedHeatmapSamples(scopedData) > 0 && <Alert showIcon type="warning" style={{ marginBottom: 8 }}
                message={`${countUnplottedHeatmapSamples(scopedData)} spatial samples could not be placed on this heatmap.`}
                description="Their measurement values remain available in numeric summaries." />}
            <div className="report-animated-heatmap-stage">
                {currentFrame && (
                    <ReportHeatmap
                        title={currentFrame.displayDate}
                        mode={mode as 'template' | 'gps'}
                        templateSize={templateSize}
                        templateImageUrl={templateImageUrl}
                        templateLinesUrl={templateLinesUrl}
                        geoJsonPreview={geoJsonPreview}
                        dataPoints={currentFrame.dataPoints}
                        grid={currentFrame.grid}
                        templateOpacity={(section.templateOpacity ?? 70) / 100}
                        heatmapOpacity={(section.heatmapOpacity ?? 90) / 100}
                        colorScheme={section.heatmapColorScheme || 'blue-red'}
                        invertColors={section.invertColors ?? false}
                        valueRange={resolvedValueRange}
                        hexagonSize={section.hexagonSizeMeters ?? 50}
                        showCard={false}
                    />
                )}

                <div className="report-animated-heatmap-controls">
                    <div className="report-animated-heatmap-controls-row">
                        <div className="report-animated-heatmap-controls-left">
                            <Button
                                icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                onClick={() => setIsPlaying(!isPlaying)}
                                type={isPlaying ? 'primary' : 'default'}
                            >
                                {isPlaying ? 'Pause' : 'Play'}
                            </Button>
                            <div className="report-animated-heatmap-speed">
                                <Text type="secondary">Speed</Text>
                                <Select
                                    value={playbackSpeed}
                                    onChange={setPlaybackSpeed}
                                    style={{ width: 118 }}
                                    options={[
                                        { label: 'Slow', value: 2000 },
                                        { label: 'Normal', value: 1000 },
                                        { label: 'Fast', value: 500 },
                                        { label: 'Very Fast', value: 250 },
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="report-animated-heatmap-controls-right">
                            <span className="report-animated-heatmap-current-date">
                                {currentFrame?.displayDate}
                            </span>
                            <span className="report-animated-heatmap-current-status">
                                {isPlaying ? 'Playing' : 'Paused'}
                            </span>
                        </div>
                    </div>

                    <div className="report-animated-heatmap-slider">
                        <Slider
                            min={0}
                            max={frames.length - 1}
                            value={currentFrameIndex}
                            onChange={(value) => {
                                setCurrentFrameIndex(value);
                                // Pause on manual interaction
                                setIsPlaying(false);
                            }}
                            marks={frames.reduce((acc: Record<number, any>, frame, idx) => {
                                const markInterval = Math.max(1, Math.floor(frames.length / 5));
                                if (idx === 0 || idx === frames.length - 1 || idx % markInterval === 0) {
                                    const date = new Date(frame.timestamp);
                                    const shortDate = isNaN(date.getTime())
                                        ? frame.displayDate
                                        : date.toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                        });
                                    acc[idx] = {
                                        label: <span className="report-animated-heatmap-mark">{shortDate}</span>,
                                    };
                                }
                                return acc;
                            }, {})}
                            tooltip={{
                                formatter: (value) => value !== undefined ? (frames[value]?.displayDate || '') : '',
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportAnimatedHeatmap;
